import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { MapPin, Calendar, User, Mail, Phone, Truck } from 'lucide-react';
export function BookingForm() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].booking;
  const [minDate, setMinDate] = useState('');
  useEffect(() => {
    // Calculate date 72 hours (3 days) from now
    const date = new Date();
    date.setDate(date.getDate() + 3);
    const formattedDate = date.toISOString().split('T')[0];
    setMinDate(formattedDate);
  }, []);
  return <section id="booking" className="py-20 bg-mf-gray relative" data-id="element-18">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-19">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-xl shadow-2xl overflow-hidden border-t-8 border-mf-red" data-id="element-20">
          <div className="bg-mf-black text-white py-6 px-8 text-center" data-id="element-21">
            <h2 className="text-3xl md:text-4xl font-condensed font-black uppercase tracking-wider flex items-center justify-center" data-id="element-22">
              <Truck className="mr-3 text-mf-yellow" size={32} data-id="element-23" />
              {text.title}
            </h2>
          </div>

          <div className="p-8 md:p-10" data-id="element-24">
            <form action="https://formsubmit.co/movefighters@gmail.com" method="POST" className="space-y-6" data-id="element-25">
              {/* Hidden FormSubmit fields */}
              <input type="hidden" name="_subject" value="New Move Fighters Booking Request" data-id="element-26" />
              <input type="hidden" name="_captcha" value="false" data-id="element-27" />
              <input type="hidden" name="_template" value="table" data-id="element-28" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-29">
                {/* Size Selection */}
                <div className="col-span-1 md:col-span-2" data-id="element-30">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-31">
                    {text.sizeLabel}
                  </label>
                  <div className="relative" data-id="element-32">
                    <select name="size" required className="block w-full pl-4 pr-10 py-4 text-base border-2 border-gray-200 focus:outline-none focus:ring-0 focus:border-mf-red sm:text-lg rounded-md appearance-none bg-gray-50 font-medium transition-colors cursor-pointer" data-id="element-33">
                      <option value="" disabled selected data-id="element-34">
                        -- {text.sizeLabel} --
                      </option>
                      {text.sizeOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-35">
                          {opt.label}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-36">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-37">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-38" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Origin */}
                <div className="col-span-1" data-id="element-39">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-40">
                    {text.originLabel}
                  </label>
                  <div className="relative" data-id="element-41">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-42">
                      <MapPin className="h-5 w-5 text-gray-400" data-id="element-43" />
                    </div>
                    <input type="text" name="origin" placeholder="City, Zip Code, or Full Address" required className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-44" />
                  </div>
                </div>

                {/* Destination */}
                <div className="col-span-1" data-id="element-45">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-46">
                    {text.destLabel}
                  </label>
                  <div className="relative" data-id="element-47">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-48">
                      <MapPin className="h-5 w-5 text-mf-red" data-id="element-49" />
                    </div>
                    <input type="text" name="destination" placeholder="City, Zip Code, or Full Address" required className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-50" />
                  </div>
                </div>

                {/* Date */}
                <div className="col-span-1 md:col-span-2" data-id="element-51">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-52">
                    {text.dateLabel}{' '}
                    <span className="text-xs text-mf-red font-normal normal-case ml-2" data-id="element-53">
                      (Min 72h notice)
                    </span>
                  </label>
                  <div className="relative" data-id="element-54">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-55">
                      <Calendar className="h-5 w-5 text-gray-400" data-id="element-56" />
                    </div>
                    <input type="date" name="date" required min={minDate} className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-57" />
                  </div>
                </div>

                {/* Name */}
                <div className="col-span-1" data-id="element-58">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-59">
                    {text.nameLabel}
                  </label>
                  <div className="relative" data-id="element-60">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-61">
                      <User className="h-5 w-5 text-gray-400" data-id="element-62" />
                    </div>
                    <input type="text" name="name" required className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-63" />
                  </div>
                </div>

                {/* Phone */}
                <div className="col-span-1" data-id="element-64">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-65">
                    {text.phoneLabel}
                  </label>
                  <div className="relative" data-id="element-66">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-67">
                      <Phone className="h-5 w-5 text-gray-400" data-id="element-68" />
                    </div>
                    <input type="tel" name="phone" required className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-69" />
                  </div>
                </div>

                {/* Email */}
                <div className="col-span-1 md:col-span-2" data-id="element-70">
                  <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-71">
                    {text.emailLabel}
                  </label>
                  <div className="relative" data-id="element-72">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-73">
                      <Mail className="h-5 w-5 text-gray-400" data-id="element-74" />
                    </div>
                    <input type="email" name="email" required className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:border-mf-red bg-gray-50 transition-colors" data-id="element-75" />
                  </div>
                </div>
              </div>

              <div className="pt-4" data-id="element-76">
                <button type="submit" className="w-full flex justify-center py-4 px-4 border border-transparent rounded-md shadow-sm text-xl font-condensed font-bold text-mf-black bg-mf-yellow hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mf-yellow uppercase tracking-wider transition-colors" data-id="element-77">
                  {text.submit}
                </button>
                <p className="text-center text-xs text-gray-500 mt-4 font-medium" data-id="element-78">
                  No credit card required. Fast response guaranteed.
                </p>
              </div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-full h-64 bg-mf-black transform -skew-y-2 origin-top-left z-0" data-id="element-79"></div>
    </section>;
}